import React, { useState } from "react";
import { SelectPicker, Checkbox, Button, Radio, RadioGroup, Input } from "rsuite";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../../Components/Header/Header";
import "./index.css";
import {
  getProdutor,
  getSafras,
  getTipoSafra,
  getCultura,
  getManutencaoLista,
  executarManutencao,
  getProdutoAdubacao,
  getProdutoAdubacaoFolhar,
  getProdutoHerbicida,
  getProdutoInseticida,
  getProdutoFungicida,
  getProdutoOutrosProdutos,
  getTratamentoProduto,
} from "../../Services/programation.service";

// Tipos de insumo e como buscar o catálogo de produtos de cada um
const TIPOS_INSUMO = [
  { value: "adubacao", label: "Adubação", fetch: getProdutoAdubacao },
  { value: "adubacaoFolhar", label: "Adubação Foliar", fetch: getProdutoAdubacaoFolhar },
  { value: "herbicida", label: "Herbicida", fetch: getProdutoHerbicida },
  { value: "inseticida", label: "Inseticida", fetch: getProdutoInseticida },
  { value: "fungicida", label: "Fungicida", fetch: getProdutoFungicida },
  { value: "outrosProdutos", label: "Outros Produtos", fetch: getProdutoOutrosProdutos },
  { value: "tratamentoSementes", label: "Tratamento de Sementes", fetch: getTratamentoProduto },
];

const selectStyle = {
  width: "100%",
  border: "solid 1px rgba(1, 104, 32, 0.81)",
  borderRadius: "5px",
};

function ManutencaoProgramacao() {
  const queryClient = useQueryClient();

  // filtros
  const [produtorId, setProdutorId] = useState(null);
  const [safraId, setSafraId] = useState(null);
  const [tipoSafraId, setTipoSafraId] = useState(null);
  const [culturaId, setCulturaId] = useState(null);
  const [tipoInsumo, setTipoInsumo] = useState(null);

  // seleção e ação
  const [cultivarFiltro, setCultivarFiltro] = useState(null); // null = todos os cultivares
  const [selecionadas, setSelecionadas] = useState([]);
  const [acao, setAcao] = useState("trocar");
  const [produtoAlvoId, setProdutoAlvoId] = useState(null);
  const [novoProdutoId, setNovoProdutoId] = useState(null);
  const [dose, setDose] = useState("");
  const [estadio, setEstadio] = useState(""); // estádio ao inserir (opcional)
  const [executando, setExecutando] = useState(false);

  const optionsMap = (res) =>
    (res?.data || [])
      .filter((x) => !x.excluido)
      .map((x) => ({ label: x.nome, value: x.id }));

  const produtoresQuery = useQuery(["mntProdutores"], async () => {
    const { res } = await getProdutor();
    return optionsMap(res);
  });
  const safrasQuery = useQuery(["mntSafras"], async () => {
    const { res } = await getSafras();
    return optionsMap(res);
  });
  const tiposQuery = useQuery(["mntTipos"], async () => {
    const { res } = await getTipoSafra();
    return optionsMap(res);
  });
  const culturasQuery = useQuery(["mntCulturas"], async () => {
    const { res } = await getCultura();
    return optionsMap(res);
  });

  const filtrosOk = !!(produtorId && safraId && tipoSafraId && culturaId && tipoInsumo);

  const listaQuery = useQuery(
    ["mntLista", produtorId, safraId, tipoSafraId, culturaId, tipoInsumo],
    async () => {
      const { res } = await getManutencaoLista({
        produtorId,
        safraId,
        tipoSafraId,
        culturaId,
        tipoInsumo,
      });
      return res?.data || [];
    },
    { enabled: filtrosOk }
  );

  // catálogo de produtos do tipo escolhido (pra inserir / trocar-por)
  const catalogoQuery = useQuery(
    ["mntCatalogo", tipoInsumo],
    async () => {
      const cfg = TIPOS_INSUMO.find((t) => t.value === tipoInsumo);
      if (!cfg) return [];
      const { res } = await cfg.fetch();
      return optionsMap(res);
    },
    { enabled: !!tipoInsumo }
  );

  const lista = listaQuery.data || [];

  // produtos que EXISTEM nos talhões listados (alvos de excluir/trocar)
  const produtosEncontrados = [];
  lista.forEach((g) =>
    (g.itens || []).forEach((i) => {
      if (i.produtoId && !produtosEncontrados.some((p) => p.value === i.produtoId)) {
        produtosEncontrados.push({ label: i.produtoNome, value: i.produtoId });
      }
    })
  );
  produtosEncontrados.sort((a, b) => String(a.label).localeCompare(String(b.label), "pt-BR"));

  // cultivares distintos que aparecem nos talhões (pra filtrar a ação — opcional)
  const cultivaresEncontrados = [];
  lista.forEach((g) =>
    (g.itens || []).forEach((i) => {
      if (i.cultivarId && !cultivaresEncontrados.some((c) => c.value === i.cultivarId)) {
        cultivaresEncontrados.push({ label: i.cultivarNome, value: i.cultivarId });
      }
    })
  );
  cultivaresEncontrados.sort((a, b) =>
    String(a.label).localeCompare(String(b.label), "pt-BR")
  );

  const todasSelecionadas = lista.length > 0 && selecionadas.length === lista.length;
  const toggleTodas = () =>
    setSelecionadas(todasSelecionadas ? [] : lista.map((g) => g.glebaId));
  const toggleUma = (glebaId) =>
    setSelecionadas((prev) =>
      prev.includes(glebaId) ? prev.filter((x) => x !== glebaId) : [...prev, glebaId]
    );

  const limparAcao = () => {
    setProdutoAlvoId(null);
    setNovoProdutoId(null);
    setDose("");
    setEstadio("");
  };

  const nomeProduto = (id) =>
    produtosEncontrados.find((p) => p.value === id)?.label ||
    (catalogoQuery.data || []).find((p) => p.value === id)?.label ||
    "?";

  const handleExecutar = async () => {
    if (selecionadas.length === 0) {
      toast.warning("Marque pelo menos um talhão na lista.");
      return;
    }
    if (!produtoAlvoId) {
      toast.warning("Escolha o produto.");
      return;
    }
    if (acao === "trocar" && !novoProdutoId && !dose) {
      toast.warning("Na troca, informe o novo produto e/ou a nova dose.");
      return;
    }
    if (acao === "inserir" && !dose) {
      toast.warning("Informe a dose do produto a inserir.");
      return;
    }

    const tipoLabel = TIPOS_INSUMO.find((t) => t.value === tipoInsumo)?.label;
    let resumo = "";
    if (acao === "excluir")
      resumo = `EXCLUIR "${nomeProduto(produtoAlvoId)}" (${tipoLabel}) de ${selecionadas.length} talhão(ões)?`;
    if (acao === "trocar")
      resumo = `TROCAR "${nomeProduto(produtoAlvoId)}"${
        novoProdutoId ? ` por "${nomeProduto(novoProdutoId)}"` : ""
      }${dose ? ` com dose ${dose}` : ""} em ${selecionadas.length} talhão(ões)?`;
    if (acao === "inserir")
      resumo = `INSERIR "${nomeProduto(produtoAlvoId)}" com dose ${dose}${
        estadio ? ` (estádio ${estadio})` : ""
      } em ${selecionadas.length} talhão(ões)?`;

    const cvNome = cultivaresEncontrados.find((c) => c.value === cultivarFiltro)?.label;
    const escopo = cvNome
      ? `\n\nSó no cultivar "${cvNome}".`
      : "\n\nEm TODOS os cultivares dessa cultura, nos talhões marcados.";
    if (!window.confirm(resumo + escopo)) return;

    setExecutando(true);
    const { res, error } = await executarManutencao({
      safraId,
      tipoSafraId,
      culturaId,
      cultivarId: cultivarFiltro || undefined, // vazio = todos os cultivares
      tipoInsumo,
      glebaIds: selecionadas,
      acao,
      produtoId: produtoAlvoId,
      novoProdutoId: acao === "trocar" ? novoProdutoId || undefined : undefined,
      dose: dose === "" ? undefined : Number(String(dose).replace(",", ".")),
      estadio: acao === "inserir" && estadio ? estadio : undefined,
    });
    setExecutando(false);

    if (res?.data) {
      const d = res.data;
      let msg = "";
      if (d.acao === "excluir") msg = `Excluído de ${d.linhas} lançamento(s) em ${d.talhoes} talhão(ões).`;
      if (d.acao === "trocar") msg = `Alterado(s) ${d.linhas} lançamento(s) em ${d.talhoes} talhão(ões).`;
      if (d.acao === "inserir")
        msg = `Inserido em ${d.linhas} bloco(s)${d.pulados ? ` (${d.pulados} já tinham o produto e foram pulados)` : ""}.`;
      toast.success(msg || "Feito!");
      limparAcao();
      setSelecionadas([]);
      queryClient.invalidateQueries(["mntLista"]);
      // A manutenção altera programações que podem estar em cache na tela inicial.
      // Sem isto, ao voltar para a programação aparecem os valores antigos.
      queryClient.invalidateQueries(["configuracaoRelatorioAddGleba"]);
    } else {
      toast.error(
        error?.response?.data?.message || "Não foi possível executar. Nada foi alterado."
      );
    }
  };

  return (
    <div className="container-home">
      <Header />
      <div className="manutencao-panel">
        <div className="manutencao-titulo">MANUTENÇÃO DE PROGRAMAÇÕES</div>

        <div className="manutencao-filtros">
          <div className="manutencao-filtro">
            <b>Produtor</b>
            <SelectPicker
              block
              data={produtoresQuery.data || []}
              value={produtorId}
              loading={produtoresQuery.isFetching}
              onChange={(v) => {
                setProdutorId(v);
                setSelecionadas([]);
              }}
              style={selectStyle}
            />
          </div>
          <div className="manutencao-filtro">
            <b>Safra</b>
            <SelectPicker
              block
              data={safrasQuery.data || []}
              value={safraId}
              onChange={(v) => {
                setSafraId(v);
                setSelecionadas([]);
              }}
              style={selectStyle}
            />
          </div>
          <div className="manutencao-filtro">
            <b>Tipo de safra</b>
            <SelectPicker
              block
              data={tiposQuery.data || []}
              value={tipoSafraId}
              onChange={(v) => {
                setTipoSafraId(v);
                setSelecionadas([]);
              }}
              style={selectStyle}
            />
          </div>
          <div className="manutencao-filtro">
            <b>Cultura</b>
            <SelectPicker
              block
              data={culturasQuery.data || []}
              value={culturaId}
              onChange={(v) => {
                setCulturaId(v);
                setSelecionadas([]);
                setCultivarFiltro(null);
              }}
              style={selectStyle}
            />
          </div>
          <div className="manutencao-filtro">
            <b>Tipo de insumo</b>
            <SelectPicker
              block
              searchable={false}
              data={TIPOS_INSUMO.map(({ value, label }) => ({ value, label }))}
              value={tipoInsumo}
              onChange={(v) => {
                setTipoInsumo(v);
                setSelecionadas([]);
                limparAcao();
              }}
              style={selectStyle}
            />
          </div>
        </div>

        {!filtrosOk ? (
          <div className="manutencao-vazio">
            Escolha produtor, safra, tipo de safra, cultura e tipo de insumo para
            ver o que está programado.
          </div>
        ) : listaQuery.isFetching ? (
          <div className="manutencao-vazio">Buscando programações...</div>
        ) : lista.length === 0 ? (
          <div className="manutencao-vazio">
            Nenhum talhão desse produtor tem programação dessa cultura nessa safra.
          </div>
        ) : (
          <>
            <table className="manutencao-tabela">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <Checkbox
                      checked={todasSelecionadas}
                      onChange={toggleTodas}
                      title="Marcar todos"
                    />
                  </th>
                  <th style={{ width: 220 }}>Talhão</th>
                  <th style={{ width: 180 }}>Fazenda</th>
                  <th>
                    Programado (
                    {TIPOS_INSUMO.find((t) => t.value === tipoInsumo)?.label})
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((g) => (
                  <tr key={g.glebaId}>
                    <td>
                      <Checkbox
                        checked={selecionadas.includes(g.glebaId)}
                        onChange={() => toggleUma(g.glebaId)}
                      />
                    </td>
                    <td>
                      <b>{g.glebaNome}</b>
                    </td>
                    <td>{g.fazendaNome}</td>
                    <td>
                      {(g.itens || []).length === 0 ? (
                        <span style={{ color: "#999" }}>— nada lançado —</span>
                      ) : (
                        g.itens.map((i) => (
                          <span className="manutencao-item-chip" key={i.id}>
                            {i.produtoNome} · {i.dose}
                            {i.cultivarNome ? ` · ${i.cultivarNome}` : ""}
                          </span>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="manutencao-acao">
              <div style={{ flex: "1 1 100%" }}>
                <b>
                  O que fazer nos {selecionadas.length} talhão(ões) marcado(s)?
                </b>
                <RadioGroup
                  inline
                  value={acao}
                  onChange={(v) => {
                    setAcao(v);
                    limparAcao();
                  }}
                >
                  <Radio value="excluir">Excluir produto</Radio>
                  <Radio value="trocar">Trocar produto / dose</Radio>
                  <Radio value="inserir">Inserir produto</Radio>
                </RadioGroup>
              </div>

              {/* Cultivar aparece só quando há mais de um — opcional */}
              {cultivaresEncontrados.length > 1 && (
                <div style={{ flex: "1 1 28%", minWidth: 200 }}>
                  <b style={{ fontSize: 13 }}>Cultivar (opcional)</b>
                  <SelectPicker
                    block
                    data={[
                      { label: "Todos os cultivares", value: "" },
                      ...cultivaresEncontrados,
                    ]}
                    value={cultivarFiltro ?? ""}
                    searchable={false}
                    cleanable={false}
                    onChange={(v) => setCultivarFiltro(v || null)}
                    style={selectStyle}
                  />
                </div>
              )}

              <div style={{ flex: "1 1 28%", minWidth: 200 }}>
                <b style={{ fontSize: 13 }}>
                  {acao === "inserir" ? "Produto a inserir" : "Produto programado"}
                </b>
                <SelectPicker
                  block
                  data={acao === "inserir" ? catalogoQuery.data || [] : produtosEncontrados}
                  value={produtoAlvoId}
                  onChange={setProdutoAlvoId}
                  style={selectStyle}
                  placeholder="Escolha o produto"
                />
              </div>

              {acao === "trocar" && (
                <div style={{ flex: "1 1 28%", minWidth: 200 }}>
                  <b style={{ fontSize: 13 }}>Novo produto (opcional se só mudar dose)</b>
                  <SelectPicker
                    block
                    data={catalogoQuery.data || []}
                    value={novoProdutoId}
                    onChange={setNovoProdutoId}
                    style={selectStyle}
                    placeholder="Produto que entra no lugar"
                  />
                </div>
              )}

              {(acao === "trocar" || acao === "inserir") && (
                <div style={{ flex: "0 1 160px", minWidth: 140 }}>
                  <b style={{ fontSize: 13 }}>
                    {acao === "trocar" ? "Nova dose (opcional)" : "Dose"}
                  </b>
                  <Input
                    type="number"
                    step="any"
                    value={dose}
                    onChange={(v) => setDose(v)}
                    placeholder="ex.: 2,5"
                  />
                </div>
              )}

              {acao === "inserir" && (
                <div style={{ flex: "0 1 180px", minWidth: 140 }}>
                  <b style={{ fontSize: 13 }}>Estádio (opcional)</b>
                  <Input
                    value={estadio}
                    onChange={(v) => setEstadio(v)}
                    placeholder="ex.: V6, R1, Dessecação"
                  />
                </div>
              )}

              <div>
                <Button
                  appearance="primary"
                  loading={executando}
                  disabled={selecionadas.length === 0}
                  style={{ background: "rgba(1, 104, 32, 0.81)", width: 170 }}
                  onClick={handleExecutar}
                >
                  EXECUTAR
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={6000} theme="light" />
    </div>
  );
}

export default ManutencaoProgramacao;
